import mapboxgl from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'
import {
  BOARD_LOCATION_DEFAULT_ZOOM,
  BOARD_LOCATION_PREVIEW_IDLE,
  BOARD_LOCATION_ZOOM_MAX,
  BOARD_LOCATION_ZOOM_MIN,
  boardCamerasEqual,
  latLngHit,
  parseLatLngQuery,
  searchBoardLocations,
  type BoardCamera,
  type GeocodeHit,
} from './boardLocation'
import { defaultMapboxToken, mapboxTokenFor, mapRefById, STREETS_STYLE_URL } from './maps'

export type BoardLocationDraft = {
  query: string
  selectedLabel: string | null
  camera: BoardCamera | null
  ready: boolean
}

type Props = {
  /** Catalog / session map id for preview style (defaults to Streets). */
  mapId?: string
  /** Starting framing (Settings: current home; wizard: idle wide view). */
  initialCamera?: BoardCamera
  /**
   * Settings: show a “Selected” label for the current home. Wizard leaves this
   * false — the live map framing is enough, search is optional.
   */
  seedAsPicked?: boolean
  /** Restore after Back → return to Where? (wizard). */
  restoreDraft?: BoardLocationDraft | null
  /**
   * Settings: show a compact Reset when the draft differs from `initialCamera`
   * (after pan/zoom/search). Restores the framing this dialog opened with.
   */
  showReset?: boolean
  /** When false (wizard), parent owns Back / Cancel / Continue. */
  showActions?: boolean
  confirmLabel?: string
  busy?: boolean
  /** Live map framing. `ready` is true as soon as the preview has a camera. */
  onCameraChange?: (camera: BoardCamera | null, ready: boolean) => void
  /** Full draft for parent persistence across Back. */
  onDraftChange?: (draft: BoardLocationDraft) => void
  onConfirm?: (camera: BoardCamera) => void
  onCancel?: () => void
}

function clampZoom(z: number): number {
  return Math.min(BOARD_LOCATION_ZOOM_MAX, Math.max(BOARD_LOCATION_ZOOM_MIN, z))
}

export function BoardLocationPicker({
  mapId = 'streets',
  initialCamera = BOARD_LOCATION_PREVIEW_IDLE,
  seedAsPicked = false,
  restoreDraft = null,
  showReset = false,
  showActions = true,
  confirmLabel = 'Use this place',
  busy = false,
  onCameraChange,
  onDraftChange,
  onConfirm,
  onCancel,
}: Props) {
  const mapElRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const skipMoveRef = useRef(false)
  const restored = restoreDraft?.ready && restoreDraft.camera ? restoreDraft : null
  const startPicked = Boolean(restored) || seedAsPicked
  const startCamera = restored?.camera ?? initialCamera
  const onCameraChangeRef = useRef(onCameraChange)
  const onDraftChangeRef = useRef(onDraftChange)
  onCameraChangeRef.current = onCameraChange
  onDraftChangeRef.current = onDraftChange

  const [query, setQuery] = useState(() => restored?.query ?? '')
  const [hits, setHits] = useState<GeocodeHit[]>([])
  const [searching, setSearching] = useState(false)
  const [listOpen, setListOpen] = useState(false)
  // Label only after an explicit pick (search / wizard restore) — not Settings seed.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(
    restored?.selectedLabel ?? null,
  )
  const [picked, setPicked] = useState(startPicked)
  const [zoom, setZoom] = useState(() =>
    clampZoom(startCamera.zoom || BOARD_LOCATION_PREVIEW_IDLE.zoom),
  )
  const [center, setCenter] = useState({ lng: startCamera.lng, lat: startCamera.lat })
  const searchGen = useRef(0)

  const camera: BoardCamera = { lng: center.lng, lat: center.lat, zoom }

  const draftDirty =
    showReset &&
    (Boolean(query.trim()) ||
      Boolean(selectedLabel) ||
      !picked ||
      !boardCamerasEqual(
        { lng: center.lng, lat: center.lat, zoom },
        {
          lng: initialCamera.lng,
          lat: initialCamera.lat,
          zoom: clampZoom(initialCamera.zoom || BOARD_LOCATION_DEFAULT_ZOOM),
        },
      ))
  const statusOpen =
    Boolean(selectedLabel) ||
    searching ||
    draftDirty ||
    query.trim().length > 0 ||
    hits.length > 0

  useEffect(() => {
    const next = { lng: center.lng, lat: center.lat, zoom }
    onCameraChangeRef.current?.(next, true)
    onDraftChangeRef.current?.({
      query,
      selectedLabel,
      camera: next,
      ready: true,
    })
  }, [center.lng, center.lat, zoom, query, selectedLabel])

  useEffect(() => {
    const el = mapElRef.current
    if (!el || mapRef.current) return
    const token = mapboxTokenFor(mapId) || defaultMapboxToken()
    if (!token) return
    mapboxgl.accessToken = token
    const styleUrl = mapRefById(mapId)?.styleUrl || STREETS_STYLE_URL
    const map = new mapboxgl.Map({
      container: el,
      style: styleUrl,
      center: [startCamera.lng, startCamera.lat],
      zoom: clampZoom(startCamera.zoom),
      minZoom: BOARD_LOCATION_ZOOM_MIN,
      maxZoom: BOARD_LOCATION_ZOOM_MAX,
      attributionControl: false,
      // Wordmark hidden via CSS (.sc-board-loc-map .mapboxgl-ctrl-logo).
      pitch: 0,
      bearing: 0,
      maxPitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    })
    map.dragRotate.disable()
    map.touchPitch.disable()
    map.touchZoomRotate.disableRotation()
    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false,
      }),
      'top-right',
    )
    const syncFromMap = () => {
      if (skipMoveRef.current) {
        skipMoveRef.current = false
        return
      }
      const c = map.getCenter()
      setCenter({ lng: c.lng, lat: c.lat })
      setZoom(clampZoom(map.getZoom()))
    }
    map.on('moveend', syncFromMap)
    const resize = () => {
      map.resize()
    }
    map.on('load', resize)
    const ro = new ResizeObserver(resize)
    ro.observe(el)
    mapRef.current = map
    return () => {
      ro.disconnect()
      map.remove()
      mapRef.current = null
    }
    // Mount once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Search only on typed query — never on pan/zoom.
  useEffect(() => {
    if (picked) {
      setHits([])
      setSearching(false)
      setListOpen(false)
      return
    }
    const q = query.trim()
    if (q.length < 2) {
      setHits([])
      setSearching(false)
      setListOpen(false)
      return
    }
    const coords = parseLatLngQuery(q)
    if (coords) {
      const gen = ++searchGen.current
      const timer = window.setTimeout(() => {
        if (gen !== searchGen.current) return
        setSearching(false)
        applyPick(latLngHit(coords.lat, coords.lng))
      }, 280)
      return () => {
        window.clearTimeout(timer)
      }
    }
    const gen = ++searchGen.current
    const ac = new AbortController()
    setSearching(true)
    const timer = window.setTimeout(() => {
      void searchBoardLocations(q, { signal: ac.signal })
        .then((rows) => {
          if (gen !== searchGen.current) return
          setHits(rows)
          setListOpen(rows.length > 0)
        })
        .catch(() => {
          if (gen !== searchGen.current) return
          setHits([])
          setListOpen(false)
        })
        .finally(() => {
          if (gen === searchGen.current) setSearching(false)
        })
    }, 280)
    return () => {
      window.clearTimeout(timer)
      ac.abort()
    }
  }, [query, picked])

  function jumpMap(next: BoardCamera): void {
    const map = mapRef.current
    if (!map) return
    skipMoveRef.current = true
    map.jumpTo({ center: [next.lng, next.lat], zoom: next.zoom })
  }

  function applyPick(hit: GeocodeHit): void {
    const nextZoom = BOARD_LOCATION_DEFAULT_ZOOM
    setSelectedLabel(hit.label)
    setPicked(true)
    setListOpen(false)
    setHits([])
    setCenter({ lng: hit.lng, lat: hit.lat })
    setZoom(nextZoom)
    jumpMap({ lng: hit.lng, lat: hit.lat, zoom: nextZoom })
  }

  function clearPick(): void {
    setPicked(false)
    setSelectedLabel(null)
  }

  function resetToInitial(): void {
    const z = clampZoom(initialCamera.zoom || BOARD_LOCATION_DEFAULT_ZOOM)
    const next = { lng: initialCamera.lng, lat: initialCamera.lat, zoom: z }
    setQuery('')
    setSelectedLabel(null)
    setHits([])
    setListOpen(false)
    setPicked(seedAsPicked || Boolean(restored))
    setCenter({ lng: next.lng, lat: next.lat })
    setZoom(z)
    jumpMap(next)
  }

  function onZoomSlider(next: number): void {
    const z = clampZoom(next)
    setZoom(z)
    const map = mapRef.current
    if (!map) return
    skipMoveRef.current = true
    map.setZoom(z)
  }

  return (
    <div className="sc-board-loc">
      <div className="sc-field sc-board-loc-search">
        <label className="sc-flbl" htmlFor="sc-board-loc-q">
          Search for a place
        </label>
        <input
          id="sc-board-loc-q"
          className="sc-fin"
          type="search"
          autoComplete="off"
          placeholder="Address, city, landmark, or lat, long"
          value={query}
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value
            setQuery(v)
            if (picked) clearPick()
          }}
          onFocus={() => {
            if (!picked && hits.length > 0) setListOpen(true)
          }}
        />
        <div
          className={`sc-board-loc-selected${statusOpen ? '' : ' is-empty'}`}
          aria-live="polite"
        >
          {picked && selectedLabel ? (
            <span>
              Selected: <b>{selectedLabel}</b>
            </span>
          ) : searching ? (
            <span className="sc-hint">Searching…</span>
          ) : (
            <span className="sc-hint">&nbsp;</span>
          )}
          {draftDirty ? (
            <button
              type="button"
              className="sc-board-loc-reset"
              disabled={busy}
              onClick={resetToInitial}
            >
              Reset
            </button>
          ) : null}
        </div>
        {listOpen && hits.length > 0 ? (
          <ul className="sc-board-loc-hits" role="listbox">
            {hits.map((hit) => (
              <li key={hit.id}>
                <button
                  type="button"
                  role="option"
                  className="sc-board-loc-hit"
                  disabled={busy}
                  onClick={() => applyPick(hit)}
                >
                  {hit.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="sc-board-loc-map" ref={mapElRef} aria-label="Map preview" />
      <div className="sc-board-loc-zoom">
        <label className="sc-flbl" htmlFor="sc-board-loc-zoom">
          Zoom
        </label>
        <input
          id="sc-board-loc-zoom"
          type="range"
          min={BOARD_LOCATION_ZOOM_MIN}
          max={BOARD_LOCATION_ZOOM_MAX}
          step={0.25}
          value={zoom}
          disabled={busy}
          onChange={(e) => onZoomSlider(Number(e.target.value))}
        />
      </div>
      <p className="sc-hint sc-board-loc-framing-hint">
        You can pan and zoom to frame the view.
      </p>
      {showActions ? (
        <div className="sc-modal-actions">
          <button type="button" className="sc-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="sc-btn go armed-green"
            disabled={busy}
            onClick={() => {
              if (camera) onConfirm?.(camera)
            }}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      ) : null}
    </div>
  )
}
